import AI_CHAT from '@/constant/aiChat.js';
import {
  saveLocalData,
  getLocalData,
  removeLocalData,
} from '@/lib/base/localData';
import i18n from '@/i18n/index.js';
import {
  upsertAiRecord2c,
  upsertAiRecord2b,
  postChatbot2c,
  postChatbot2b,
} from '@/services/ai.js';
import dayjs from 'dayjs';
import _ from 'lodash';

/**
 * getChatKey
 * @param {string} mode - The mode of the AI chat (e.g., NORMAL, TEACHER).
 * @returns {string} - The key used to store chat data in local storage.
 */
const getChatKey = (mode) => {
  return `AI_CHAT_KEY_${mode}`;
};

/**
 * getOldChatList
 * @param {string} mode - The mode of the AI chat (e.g., NORMAL, TEACHER).
 * @returns {Array} - The list of old chat messages.
 */
const getOldChatList = (mode) => {
  const key = getChatKey(mode);
  let chatList = getLocalData(key);
  try {
    chatList = JSON.parse(chatList);
    const cleanTime = dayjs().subtract(1, 'day').startOf('day').valueOf();
    chatList = chatList.filter((item) => item.time > cleanTime);
  } catch (e) {
    chatList = [];
  }
  return chatList;
};

const state = {
  chatMode: null,
  chatList: [],
};

const getters = {
  isAiChatShow(state) {
    return !!state.chatMode;
  },
  aiChatMode(state) {
    return state.chatMode;
  },
  chatList(state) {
    return state.chatList;
  },
};

const actions = {
  initChatList({commit}, chatList) {
    commit('setChatList', chatList);
  },
  openAiChat({commit}) {
    const mode = AI_CHAT.MODES.NORMAL;
    let chatList = getOldChatList(mode);
    if (!chatList?.length) {
      chatList = [
        {
          time: Date.now(),
          message: i18n.t('哈囉，我是你的小幫手！有什麼需要我協助的呢？'),
          isAi: true,
        },
      ];
    }
    commit('setChatMode', mode);
    commit('setChatList', chatList);
  },
  openTeacherAiChat({commit}) {
    const mode = AI_CHAT.MODES.TEACHER;
    let chatList = getOldChatList(mode);
    if (!chatList?.length) {
      chatList = [
        {
          time: Date.now(),
          message: i18n.t('哈囉，我是你的教學助手！有什麼需要我協助的呢？'),
          isAi: true,
        },
      ];
    }
    commit('setChatMode', mode);
    commit('setChatList', chatList);
  },
  closeAiChat({commit}) {
    commit('setChatMode', false);
  },
  async sendChat({state, commit}, {message}) {
    const id = Date.now();
    const chatList1 = [
      ...state.chatList,
      {message, time: id},
      {isAi: true, id, time: id},
    ];
    commit('setChatList', chatList1);
    const sendChat =
      state.chatMode === AI_CHAT.MODES.NORMAL ? postChatbot2c : postChatbot2b;
    const response = await sendChat(message);
    const answer = response?.answer;
    const chatList2 = [...state.chatList].map((item) => {
      if (item.id === id && item.isAi) {
        return {...item, message: answer};
      }
      return item;
    });
    commit('setChatList', chatList2);
  },

  async rateAiResponse({state, commit}, {chat, isLike}) {
    const chatList = [...state.chatList];
    const index = chatList.findIndex((item) => item === chat);
    const chatItem = chatList[index];
    if (chatItem) {
      if (chatItem.record && chatItem.record.isLike === isLike) {
        isLike = undefined;
        delete chatItem.record;
      } else {
        chatItem.record = {isLike};
      }

      commit('setChatList', chatList);
      if (chatItem.rateFunction) {
        return chatItem.rateFunction(isLike);
      } else {
        const upsertAiRecord =
          state.chatMode === AI_CHAT.MODES.NORMAL
            ? upsertAiRecord2c
            : upsertAiRecord2b;
        chatItem.rateFunction = _.debounce(async (isLike) => {
          const contents = chatList.filter(
            (_, i) => i <= index && i >= index - 5
          );
          await upsertAiRecord({
            time: chatItem.time,
            contents,
            isLike,
          });
        }, 2000);
        return chatItem.rateFunction(isLike);
      }
    }
  },
};

const mutations = {
  setChatMode(state, value) {
    state.chatMode = value;
  },
  setChatList(state, chatList) {
    state.chatList = chatList;
    const length = chatList.length;
    chatList = chatList.filter((item) => item.message);
    if (length > 30) {
      chatList = chatList.slice(length - 30, length);
    }
    saveLocalData(getChatKey(state.chatMode), JSON.stringify(chatList));
  },
  clearChatList(state) {
    state.chatList = [];
    AI_CHAT.MODES.forEach((mode) => {
      const key = getChatKey(mode);
      removeLocalData(key);
    });
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
